import { NgZone } from '@angular/core';

export interface LiveRefreshOptions {
  /** Fetches fresh data. Runs inside Angular's zone. */
  refresh: () => void;
  /** Milliseconds until the next refresh; read again after every run. */
  interval: () => number;
}

/** After the last pointer, key or scroll activity, wait this long before refreshing. */
const QUIET_MS = 1_500;
/** While someone is busy, look again after this long. */
const BUSY_RETRY_MS = 2_000;
/** What counts as someone being mid-action. */
const ACTIVITY_EVENTS = ['pointermove', 'pointerdown', 'keydown', 'wheel', 'scroll'] as const;
/** Open menus, lists and dialogs: a refresh would move things under them. */
const OPEN_LAYER = '[role="menu"], [role="listbox"], [aria-modal="true"], .cdk-overlay-pane';

/**
 * Keeps a list current without anyone asking.
 *
 * - The timer runs outside Angular's zone, so pages still settle between runs.
 * - Hidden tabs do not refresh; coming back catches up straight away if a run
 *   was missed.
 * - It holds off while someone is mid-action (moving the pointer, typing,
 *   scrolling, or with a menu or dialog open), so rows never jump under a
 *   click that is on its way.
 */
export class LiveRefresh {
  private timer?: ReturnType<typeof setTimeout>;
  private nextAt = 0;
  private lastRun = 0;
  private lastActivity = 0;
  private running = false;

  private readonly onVisibility = () => this.visibilityChanged();
  private readonly onActivity = () => (this.lastActivity = Date.now());

  constructor(
    private readonly zone: NgZone,
    private readonly options: LiveRefreshOptions
  ) {}

  start(): void {
    if (this.running || typeof document === 'undefined') return;
    this.running = true;
    this.lastRun = Date.now();
    this.zone.runOutsideAngular(() => {
      document.addEventListener('visibilitychange', this.onVisibility);
      for (const type of ACTIVITY_EVENTS) {
        document.addEventListener(type, this.onActivity, { capture: true, passive: true });
      }
    });
    this.schedule(this.options.interval());
  }

  /**
   * The data changed in a way that may want a faster pace (a send started):
   * bring the next run forward if the interval now says it is due sooner.
   */
  nudge(): void {
    if (!this.running || document.hidden) return;
    const due = this.lastRun + this.options.interval();
    if (due < this.nextAt) this.schedule(Math.max(0, due - Date.now()));
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    clearTimeout(this.timer);
    document.removeEventListener('visibilitychange', this.onVisibility);
    for (const type of ACTIVITY_EVENTS) {
      document.removeEventListener(type, this.onActivity, { capture: true });
    }
  }

  private schedule(ms: number): void {
    clearTimeout(this.timer);
    this.nextAt = Date.now() + ms;
    this.timer = this.zone.runOutsideAngular(() => setTimeout(() => this.tick(), ms));
  }

  private tick(): void {
    if (!this.running || document.hidden) return; // the visibility handler resumes
    if (this.busy()) {
      this.schedule(BUSY_RETRY_MS);
      return;
    }
    this.lastRun = Date.now();
    this.zone.run(() => this.options.refresh());
    this.schedule(this.options.interval());
  }

  private busy(): boolean {
    return Date.now() - this.lastActivity < QUIET_MS || !!document.querySelector(OPEN_LAYER);
  }

  private visibilityChanged(): void {
    if (!this.running) return;
    if (document.hidden) {
      clearTimeout(this.timer);
      return;
    }
    const due = this.options.interval() - (Date.now() - this.lastRun);
    this.schedule(Math.max(0, due));
  }
}
